import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../../../core/common/collapse-header";
import { all_routes } from "../../../../router/all_routes";
import { checkPermission } from "../../../../../core/permissons/CheckPermissons";
import apiDefinitions from "../../../../../core/api/apiDefinitions";
import Button from "react-bootstrap/Button";
import VehicleDetails from "./VehicleDetails";
import VehicleEdit from "./VehicleEdit";
import VehicleEmissionTest from "./VehicleEmissionTest";
import VehicleInsurance from "./VehicleInsurance";
import VehicleRevenueLicence from "./VehicleRevenueLicence";
import AssignParkingSlotModal from "./AssignParkingSlotModal";
import Swal from "sweetalert2";

// Define interfaces based on backend GetVehiclesForResponse
export interface VehicleDto {
  vehicleId: string;
  vehicleType: string | null;
  vehicleBrand: string | null;
  vehicleModel: string | null;
  vehicleClass: string | null;
  vehicleCondition: string | null;
  vehicleGearType: string | null;
  vehicleFeatures: string[] | null;
  vehicleFuelType: string | null;
  engineCapacity: number | null;
  numberPlate: string;
  ownerName: string | null;
  ownerAddress: string | null;
  noOfPassengers: number | null;
  assignedParkingSlotName: string | null;
  manufacturingYear: number | null;
  chassisNo: string | null;
  engineNo: string | null;
  grossWeight: number | null;
  value: number | null;
  noOfKms: number | null;
  inStationEconomy: number | null;
  outStationEconomy: number | null;
  rentPerKm: number | null;
  tyreSize: string | null;
  frontTyreSize: string | null;
  rearTyreSize: string | null;
  color: string | null;
  generalServiceStandardKms: number | null;
  lastGeneralServiceKms: number | null;
  lastGeneralServiceDate: string | null;
  advancedServiceStandardKms: number | null;
  lastAdvancedServiceKms: number | null;
  lastAdvancedServiceDate: string | null;
  standardKmsForTyreChange: number | null;
  lastTyreChangeKms: number | null;
  lastTyreChangeDate: string | null;
  vehicleStatus: string | null;
  images: string[] | null;
  bookImages: string[] | null;
  createdDate: string | null;
  createdBy: string | null;
  // internal flag for UI loading state in modal
  __loading?: boolean;
  [key: string]: any;
}

interface NavItem {
  label: string;
  icon: string;
  link: string;
  permission: string;
}

export interface ApiResponse<T> {
  status: string;
  statusDescription: string;
  data: T | null;
}

const route = all_routes;

// Defensive mapper (handles nulls and arrays safely)
const mapApiVehicleToDto = (v: any): VehicleDto => ({
  vehicleId: String(v?.vehicleId ?? ""),
  vehicleType: v?.vehicleType ?? null,
  vehicleBrand: v?.vehicleBrand ?? null,
  vehicleModel: v?.vehicleModel ?? null,
  vehicleClass: v?.vehicleClass ?? null,
  vehicleCondition: v?.vehicleCondition ?? null,
  vehicleGearType: v?.vehicleGearType ?? null,
  vehicleFeatures: Array.isArray(v?.vehicleFeatures) ? v.vehicleFeatures : null,
  vehicleFuelType: v?.vehicleFuelType ?? null,
  engineCapacity: v?.engineCapacity ?? null,
  numberPlate: String(v?.numberPlate ?? ""),
  ownerName: v?.ownerName ?? null,
  ownerAddress: v?.ownerAddress ?? null,
  noOfPassengers: v?.noOfPassengers ?? null,
  assignedParkingSlotName: v?.assignedParkingSlotName ?? null,
  manufacturingYear: v?.manufacturingYear ?? null,
  chassisNo: v?.chassisNo ?? null,
  engineNo: v?.engineNo ?? null,
  grossWeight: v?.grossWeight ?? null,
  value: v?.value ?? null,
  noOfKms: v?.noOfKms ?? null,
  inStationEconomy: v?.inStationEconomy ?? null,
  outStationEconomy: v?.outStationEconomy ?? null,
  rentPerKm: v?.rentPerKm ?? null,
  tyreSize: v?.tyreSize ?? null,
  frontTyreSize: v?.frontTyreSize ?? null,
  rearTyreSize: v?.rearTyreSize ?? null,
  color: v?.color ?? null,
  generalServiceStandardKms: v?.generalServiceStandardKms ?? null,
  lastGeneralServiceKms: v?.lastGeneralServiceKms ?? null,
  lastGeneralServiceDate: v?.lastGeneralServiceDate ?? null,
  advancedServiceStandardKms: v?.advancedServiceStandardKms ?? null,
  lastAdvancedServiceKms: v?.lastAdvancedServiceKms ?? null,
  lastAdvancedServiceDate: v?.lastAdvancedServiceDate ?? null,
  standardKmsForTyreChange: v?.standardKmsForTyreChange ?? null,
  lastTyreChangeKms: v?.lastTyreChangeKms ?? null,
  lastTyreChangeDate: v?.lastTyreChangeDate ?? null,
  vehicleStatus: v?.vehicleStatus ?? null,
  images: Array.isArray(v?.images) ? v.images : null,
  bookImages: Array.isArray(v?.bookImages) ? v.bookImages : null,
  createdDate: v?.createdDate ?? null,
  createdBy: v?.createdBy ?? null,
});

const VehicleView: React.FC = () => {
  const [vehicles, setVehicles] = useState<VehicleDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleDto | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showEmissionTestModal, setShowEmissionTestModal] = useState<boolean>(false);
  const [showInsuranceModal, setShowInsuranceModal] = useState<boolean>(false);
  const [showRevenueLicenceModal, setShowRevenueLicenceModal] = useState<boolean>(false);

  // NEW: allocation modal
  const [showAssignParkingModal, setShowAssignParkingModal] = useState<boolean>(false);
  const [vehicleForAllocation, setVehicleForAllocation] = useState<VehicleDto | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [filterType, setFilterType] = useState<string>("ALL");
  const [filterValue, setFilterValue] = useState<string>("");
  const [retryAttempts, setRetryAttempts] = useState<number>(0);
  const pageSize = 15;

  const navItems: NavItem[] = [
    { label: "View Dashboard", icon: "ti ti-layout-dashboard", link: route.VehicleDashboard, permission: "ADMINISTRATION_ACCESS" },
    { label: "View Vehicles", icon: "ti ti-car", link: route.VehicleView, permission: "VEHICLE_MANAGER_ACCESS" },
    { label: "Add Vehicle", icon: "ti ti-settings-cog", link: route.addvehicle, permission: "ADMINISTRATION_ACCESS" },
    { label: "Add Master Data", icon: "ti ti-car", link: route.AddMasterData, permission: "VEHICLE_MANAGER_ACCESS" },
  ];

  const filterKeyMap: { [key: string]: keyof VehicleDto } = {
    numberPlate: "numberPlate",
    vehicleType: "vehicleType",
    noOfPassengers: "noOfPassengers",
    manufacturingYear: "manufacturingYear",
    chassisNo: "chassisNo",
    vehicleFuelType: "vehicleFuelType",
    color: "color",
    vehicleStatus: "vehicleStatus",
  };
// Helper function — detects if vehicle is Electric
const isElectricVehicle = (fuelType: string | null | undefined) => (fuelType || '') === 'Electric';

  const fetchVehicles = useCallback(
    async (page: number, filter: { filterType: string; filterValue: string }) => {
      try {
        setLoading(true);
        setError(null);
        const payload = { filterType: filter.filterType, filterValue: filter.filterValue };
        const response = await apiDefinitions.getVehicles(page, payload);
        const data: ApiResponse<VehicleDto[]> = response.data;

        if (data.status === "S1000" && Array.isArray(data.data)) {
          let filteredVehicles = data.data;
          if (filter.filterType !== "ALL" && filter.filterValue) {
            const key = (filterKeyMap[filter.filterType] || filter.filterType) as keyof VehicleDto;
            filteredVehicles = filteredVehicles.filter((vehicle) => {
              const value = vehicle[key];
              if (Array.isArray(value)) {
                return value.some((item) =>
                  String(item).toLowerCase().includes(filter.filterValue.toLowerCase())
                );
              }
              return String(value ?? "").toLowerCase().includes(filter.filterValue.toLowerCase());
            });
          }
          const startIndex = (page - 1) * pageSize;
          const paginatedVehicles = filteredVehicles.slice(startIndex, startIndex + pageSize);
          setVehicles(paginatedVehicles);
          setTotalRecords(filteredVehicles.length);
          setRetryAttempts(0);

          if (paginatedVehicles.length === 0 && filteredVehicles.length > 0) {
            setError(`No vehicles on page ${page}. Total records: ${filteredVehicles.length}.`);
          } else if (filteredVehicles.length === 0) {
            setError("No vehicles found matching the filter criteria.");
          }
        } else {
          setError(data.statusDescription || `Failed to fetch vehicles (Status: ${data.status})`);
          setVehicles([]);
          setTotalRecords(0);
        }
      } catch (err: any) {
        const errorMessage = err.response?.data?.statusDescription || err.message || "Unknown error";
        setError(`Error fetching vehicles: ${errorMessage}`);
        setVehicles([]);
        setTotalRecords(0);
        if (retryAttempts < 2) {
          setRetryAttempts((prev) => prev + 1);
          setCurrentPage(1);
        }
      } finally {
        setLoading(false);
      }
    },
    [retryAttempts]
  );

  useEffect(() => {
    fetchVehicles(currentPage, { filterType, filterValue });
  }, [fetchVehicles, currentPage, filterType, filterValue]);

  // Refetch fresh vehicle on View click using numberPlate
  const handleViewClick = async (vehicle: VehicleDto) => {
    setSelectedVehicle({ ...vehicle, __loading: true });
    setShowDetailsModal(true);

    try {
      const res = await apiDefinitions.getVehicleByNumberPlate(vehicle.numberPlate);
      const apiData = res?.data?.data;
      const fresh = mapApiVehicleToDto(apiData);
      setSelectedVehicle((prev) => ({ ...(prev ?? vehicle), ...fresh, __loading: false }));
    } catch (e) {
      setSelectedVehicle((prev) => ({ ...(prev ?? vehicle), __loading: false }));
    }
  };

  const handleEditClick = (vehicle: VehicleDto) => {
    setSelectedVehicle(vehicle);
    setShowEditModal(true);
  };

  // NEW: open assign modal
  const openAssignModal = (vehicle: VehicleDto) => {
    setVehicleForAllocation(vehicle);
    setShowAssignParkingModal(true);
  };

  // NEW: remove allocation
  const handleRemoveAllocation = async (vehicle: VehicleDto) => {
    const result = await Swal.fire({
      title: "Remove allocated slot?",
      text: vehicle.assignedParkingSlotName
        ? `This will unassign slot "${vehicle.assignedParkingSlotName}" from ${vehicle.numberPlate}.`
        : `This will unassign the allocated slot from ${vehicle.numberPlate}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!result.isConfirmed) return;

    try {
      await apiDefinitions.removePermanentlyAllocatedParkingSlot(vehicle.vehicleId);

      // reflect immediately in table state
      setVehicles((prev) =>
        prev.map((v) =>
          v.vehicleId === vehicle.vehicleId ? { ...v, assignedParkingSlotName: null } : v
        )
      );

      // also update selectedVehicle if open
      if (selectedVehicle?.vehicleId === vehicle.vehicleId) {
        setSelectedVehicle({ ...selectedVehicle, assignedParkingSlotName: null });
      }

      await Swal.fire({
        icon: "success",
        title: "Removed",
        text: "Parking slot has been unassigned.",
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err) {
     
      await Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Failed to remove allocated parking slot.",
      });
    }
  };

  // NEW: when allocation succeeds from modal
  const handleAllocationSuccess = (vehicleId: string, slotName: string) => {
    // update table row
    setVehicles((prev) =>
      prev.map((v) => (v.vehicleId === vehicleId ? { ...v, assignedParkingSlotName: slotName } : v))
    );
    // update details modal context if open
    if (selectedVehicle?.vehicleId === vehicleId) {
      setSelectedVehicle({ ...selectedVehicle, assignedParkingSlotName: slotName });
    }
    setShowAssignParkingModal(false);
    setVehicleForAllocation(null);

    Swal.fire({
      icon: "success",
      title: "Allocated",
      text: `Slot "${slotName}" has been allocated successfully.`,
      timer: 1600,
      showConfirmButton: false,
    });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= Math.ceil(totalRecords / pageSize)) {
      setCurrentPage(page);
      setRetryAttempts(0);
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "filterType") {
      setFilterType(value);
      setFilterValue("");
      setCurrentPage(1);
      setRetryAttempts(0);
    } else if (name === "filterValue") {
      setFilterValue(value);
      setCurrentPage(1);
      setRetryAttempts(0);
    }
  };

  const handleEmissionTestClick = (vehicle: VehicleDto) => {
    setSelectedVehicle(vehicle);
    setShowEmissionTestModal(true);
  };

  const handleInsuranceClick = (vehicle: VehicleDto) => {
    setSelectedVehicle(vehicle);
    setShowInsuranceModal(true);
  };

  const handleRevenueLicenceClick = (vehicle: VehicleDto) => {
    setSelectedVehicle(vehicle);
    setShowRevenueLicenceModal(true);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">Vehicle Management/Vehicles/View Vehicles</h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body pb-0 pt-2">
                <ul className="nav nav-tabs nav-tabs-bottom">
                  {navItems.map((item, index) =>
                    item.permission && checkPermission(item.permission) ? (
                      <li className="nav-item me-3" key={index}>
                        <Link
                          to={item.link}
                          className={`nav-link px-0 ${index === 1 ? "active" : ""}`}
                        >
                          <i className={item.icon}></i> {item.label}
                        </Link>
                      </li>
                    ) : null
                  )}
                </ul>
              </div>
            </div>

            <div className="row">
              <div className="col-md-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between">
                    <div className="card-title">
                      Vehicle List (Page {currentPage} of {Math.ceil(totalRecords / pageSize)})
                    </div>
                    <div className="d-flex">
                      <select
                        className="form-select me-2"
                        name="filterType"
                        value={filterType}
                        onChange={handleFilterChange}
                        style={{ width: "150px" }}
                      >
                        <option value="ALL">All</option>
                        <option value="numberPlate">Number Plate</option>
                        <option value="vehicleType">Vehicle Type</option>
                        <option value="noOfPassengers">Passengers</option>
                        <option value="manufacturingYear">Year</option>
                        <option value="chassisNo">Chassis No</option>
                        <option value="vehicleFuelType">Fuel Type</option>
                        <option value="color">Color</option>
                        <option value="vehicleStatus">Status</option>
                      </select>
                      {filterType !== "ALL" && (
                        <input
                          type="text"
                          className="form-control"
                          name="filterValue"
                          value={filterValue}
                          onChange={handleFilterChange}
                          placeholder={`Enter ${filterType.replace(/([A-Z])/g, " $1").toLowerCase()}`}
                          style={{ width: "200px" }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="card-body">
                    {loading && <div className="text-center">Loading...</div>}
                    {error && <div className="alert alert-danger">{error}</div>}
                    {!loading && !error && vehicles.length === 0 && (
                      <div className="alert alert-info">
                        No vehicles found. Try adjusting filters or contact support.
                      </div>
                    )}

                    {!loading && !error && vehicles.length > 0 && (
                      <div className="table-responsive">
                        <table className="table text-nowrap">
                          <thead className="table-primary">
                            <tr>
                              <th>Number Plate</th>
                              <th>V. Type</th>
                              <th>Model</th>
                              <th>PA</th>
                              <th>Status</th>
                              <th>Fuel Type</th>
                              <th>Gear Type</th>
                              {/* NEW: Parking slot column */}
                              <th>Parking Slot</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {vehicles.map((vehicle, index) => (
                              <tr key={index}>
                                <td>{vehicle.numberPlate}</td>
                                <td>{vehicle.vehicleType || "N/A"}</td>
                                <td>{vehicle.vehicleModel || "N/A"}</td>
                                <td>{vehicle.noOfPassengers || 0}</td>
                                <td>{vehicle.vehicleStatus || "N/A"}</td>
                                <td>{vehicle.vehicleFuelType || "N/A"}</td>
                                <td>{vehicle.vehicleGearType || "N/A"}</td>

                                {/* NEW: Parking slot */}
                                <td>
                                  {vehicle.assignedParkingSlotName ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="badge bg-secondary border border-success-subtle">
                                        {vehicle.assignedParkingSlotName}
                                      </span>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleRemoveAllocation(vehicle)}
                                        disabled={!checkPermission("ROLE_GET_PARKING_SLOT")}
                                        title="Remove allocated slot"
                                      >
                                        Remove
                                      </Button>
                                    </div>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline-success"
                                      onClick={() => openAssignModal(vehicle)}
                                      disabled={!checkPermission("ROLE_GET_PARKING_SLOT")}
                                      title="Allocate a parking slot"
                                    >
                                      Allocate
                                    </Button>
                                  )}
                                </td>

                                <td>
                                    <div className="hstack gap-2 fs-20">
                                      <Link
                                        to="#"
                                        className="btn btn-icon btn-sm btn-soft-success rounded-pill"
                                        onClick={() => handleViewClick(vehicle)}
                                      >
                                        <i className="feather-eye" />
                                      </Link>
                                      <Link
                                        to="#"
                                        className="btn btn-icon btn-sm btn-soft-info rounded-pill"
                                        onClick={() => handleEditClick(vehicle)}
                                      >
                                        <i className="feather-edit" />
                                      </Link>
                                      <Link
                                        to="#"
                                        className="btn btn-icon btn-sm btn-soft-warning rounded-pill"
                                        onClick={() => handleRevenueLicenceClick(vehicle)}
                                      >
                                        <i className="feather-file-text" />
                                      </Link>
                                      <Link
                                        to="#"
                                        className="btn btn-icon btn-sm btn-soft-primary rounded-pill"
                                        onClick={() => handleInsuranceClick(vehicle)}
                                      >
                                        <i className="feather-shield" />
                                      </Link>

                                      {/* ONLY SHOW EMISSION TEST BUTTON IF NOT ELECTRIC */}
                                      {!isElectricVehicle(vehicle.vehicleFuelType) && (
                                        <Link
                                          to="#"
                                          className="btn btn-icon btn-sm btn-soft-danger rounded-pill"
                                          onClick={() => handleEmissionTestClick(vehicle)}
                                          title="Emission Test (Not required for Electric)"
                                        >
                                          <i className="feather-bar-chart" />
                                        </Link>
                                      )}

                                      {/* OPTIONAL: Show "Exempt" badge for Electric
                                      {isElectricVehicle(vehicle.vehicleFuelType) && (
                                        <span
                                          className="btn btn-icon btn-sm btn-soft-success rounded-pill"
                                          title="Emission Test Exempt (Electric Vehicle)"
                                          style={{ cursor: 'default' }}
                                        >
                                          <i className="feather-check-circle" />
                                        </span>
                                      )} */}
                                    </div>
                                  </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="d-flex justify-content-between mt-3">
                      <Button
                        variant="outline-primary"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </Button>
                      <span>
                        Page {currentPage} of {Math.ceil(totalRecords / pageSize)}
                      </span>
                      <Button
                        variant="outline-primary"
                        disabled={currentPage >= Math.ceil(totalRecords / pageSize)}
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {selectedVehicle && (
              <>
                <VehicleDetails
                  show={showDetailsModal}
                  onHide={() => setShowDetailsModal(false)}
                  vehicle={selectedVehicle}
                />
                <VehicleEdit
                  show={showEditModal}
                  onHide={() => setShowEditModal(false)}
                  vehicle={selectedVehicle}
                  onSave={(updatedVehicle) => {
                    setVehicles((vs) =>
                      vs.map((v) => (v.vehicleId === updatedVehicle.vehicleId ? updatedVehicle : v))
                    );
                    setShowEditModal(false);
                  }}
                />
                <VehicleRevenueLicence
                  show={showRevenueLicenceModal}
                  onHide={() => setShowRevenueLicenceModal(false)}
                  numberPlate={selectedVehicle.numberPlate}
                  vehicleClass={selectedVehicle.vehicleClass}
                  fuelType={selectedVehicle.vehicleFuelType}
                  ownerName={selectedVehicle.ownerName}
                  ownerAddress={selectedVehicle.ownerAddress}
                  grossWeight={selectedVehicle.grossWeight}
                />
                <VehicleInsurance
                  show={showInsuranceModal}
                  onHide={() => setShowInsuranceModal(false)}
                  numberPlate={selectedVehicle.numberPlate}
                />
                <VehicleEmissionTest
                  show={showEmissionTestModal && !isElectricVehicle(selectedVehicle?.vehicleFuelType ?? null)}
                  onHide={() => setShowEmissionTestModal(false)}
                  numberPlate={selectedVehicle?.numberPlate || ''}
                  vehicleClass={selectedVehicle?.vehicleClass}
                  fuelType={selectedVehicle?.vehicleFuelType}
                />
              </>
            )}

            {/* NEW: Assign Parking Slot Modal */}
            {showAssignParkingModal && vehicleForAllocation && (
              <AssignParkingSlotModal
                show={showAssignParkingModal}
                onHide={() => {
                  setShowAssignParkingModal(false);
                  setVehicleForAllocation(null);
                }}
                vehicleId={vehicleForAllocation.vehicleId}
                onAllocated={(slotName) => handleAllocationSuccess(vehicleForAllocation.vehicleId, slotName)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleView;
